<template>
  <div id="ticketEditor" class="vh80">
    <v-card
      :loading="isLoading"
      class="tickets-container screen-height-container flex-content"
      v-bind:class="[compact ? 'fullHeight' : '']"
    >
      <v-card-title class="tickets-title">
        <h2 style="width: calc(100% - 120px)">
          {{ selectedTicket || ticketId ? "Edit Ticket" : "New Ticket" }}
          <span v-if="titleStr">for {{ titleStr }}</span>
        </h2>
        <v-spacer />
        <help-button help-topic-key="dashboards-submitting-trouble-tickets" />
        <v-btn icon @click="$emit('close')"><v-icon>close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="tickets-container">
        <v-form v-model="isValid" class="h-100" ref='newTicketForm'>
          <div class="pt-3 pl-3 pr-3 new-ticket-container h-100">
            <v-row no-gutters>
              <v-col class="pr-1 pb-1" md="6">
                <v-select
                  v-model="selectedNewTicketStatus"
                  :items="ticketStatuses"
                  item-text="label"
                  item-value="id"
                  label="Ticket Status"
                  :disabled="!selectedTicket && !ticketId"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col class="pl-1 pb-1" md="6">
                <v-autocomplete
                  v-model="hardware_item_ticket_problemType_id"
                  :items="hardwareItemTicketProblemTypes"
                  item-text="label"
                  item-value="id"
                  label="Type of Problem"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col class="pr-1" md="6">
                <vuetify-date-picker
                  v-model="failureDate"
                  label="Failure Date"
                  :max="new Date().toISOString().substr(0, 10)"
                />
              </v-col>
              <v-col class="pl-1" md="6">
                <vuetify-time-picker
                  v-model="failureTime"
                  label="Failure Time"
                />
              </v-col>
            </v-row>

            <!-- note - this combobox will throw vue warnings - seems to be a vuetify bug -->

            <v-combobox
              v-if="showDevices"
              :items="hardwareDevices"
              v-model="selectedHardwareItems"
              label="Select or type the name of the malfunctioning device(s)"
              item-value="id"
              item-text="label"
              clearable
              :multiple="isBatchMode"
              :chips="isBatchMode"
              :deletable-chips="isBatchMode"
              :rules='[rules.required]'
            />

            <v-combobox
              v-if="showSopSelector"
              :items="sops"
              v-model="selectedSops"
              label="Select SOPs from the list or type the names of new ones, pressing [enter] after each"
              item-value="id"
              item-text="label"
              clearable
              :multiple="isBatchMode"
              :chips="isBatchMode"
              :deletable-chips="isBatchMode"
            />

            <v-select
              v-if="showSopSelector"
              :items="facilityWorkers"
              v-model="selectedFacilityWorkerId"
              label="Select worker who was not in compliance with SOP"
              item-value="id"
              item-text="label"
              clearable
            />

            <v-select
              item-text="label"
              item-value="id"
              label="Subcomponent"
              :items="subcomponents"
              v-model="selectedSubcomponent"
              clearable
              v-show="subcomponents.length > 0 && displaySubcomponents"
            />

            <v-text-field
              label="Ticket Title"
              v-model="label"
              counter="255"
              class="pb-1"
              :rules="[rules.required]"
            />

            <v-row justify="space-around" no-gutters>
              <v-col cols="6" md="3">
                <upload-image
                  v-model="imageUrl"
                  :imageUploadInfo="imageUploadInfo"
                  :numberUpload="1"
                />
              </v-col>
              <v-col cols="6" md="3">
                <upload-image
                  v-model="imageUrl2"
                  :imageUploadInfo="imageUploadInfo"
                  :numberUpload="2"
                />
              </v-col>
              <v-col class="hidden-sm-and-down" cols="3">
                <upload-image
                  v-model="imageUrl3"
                  :imageUploadInfo="imageUploadInfo"
                  :numberUpload="3"
                />
              </v-col>
              <v-col class="hidden-sm-and-down" cols="3">
                <upload-image
                  v-model="imageUrl4"
                  :imageUploadInfo="imageUploadInfo"
                  :numberUpload="4"
                />
              </v-col>
            </v-row>

            <v-textarea
              outlined
              label="Description"
              v-model="ticketDescription"
              class="ticket-description"
              
            />

            <v-row no-gutters>
              <v-col md="9" class="pr-1">
                <v-autocomplete
                  dense
                  chips
                  clearable
                  deletable-chips
                  multiple
                  small-chips
                  v-model="selectedUsers"
                  :items="assignableUsers"
                  return-object
                  label="Assigned Users"
                  item-text="name"
                  item-value="id"
                >
                </v-autocomplete>
              </v-col>
              <v-col md="3" class="pl-1">
                <v-select
                  dense
                  :items="ticketPriorities"
                  item-text="label"
                  item-value="id"
                  v-model="selectedPriority"
                  label="Priority"
                />
              </v-col>
            </v-row>

            <div v-if="(Trimaster.isSoftwareDev(getUser) || Trimaster.isAdmin(getUser)) && isSoftwareTicket">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-select
                    dense
                    :items="allFacilities"
                    item-text="name"
                    item-value="id"
                    v-model="selectedFacilityId"
                    label="Reporting Facility"
                  />
                </v-col>
              </v-row>
            </div>

            <div
              v-if="
                Trimaster.isAdmin(getUser) ||
                Trimaster.isUserInRole(getUser,'Maintenance')
              "
            >
              <v-row no-gutters>
                <v-col cols="6" class="pr-1">
                  <v-select
                    item-text="label"
                    item-value="id"
                    label="How was this reported?"
                    :items="reportSources"
                    v-model="selectedSource"
                    :rules="[rules.required]"
                  />
                </v-col>
                <v-col cols="6" class="pl-1">
                  <vuetify-date-picker
                    v-model="estimatedCompletionDate"
                    label="Estimated Completion Date"
                  />
                </v-col>
              </v-row>

              <v-checkbox
                v-model="adminOnly"
                label="Private - Install or Commissioning Problem"
              />
            </div>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions class="justify-center pb-1">
        <v-btn
          @click="saveTicket"
          color="primary"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import moment from "moment";
import vuetifyToast from "@/vuetifyToast.js";
import Trimaster from "@/plugins/servers/trimaster.js";
import VuetifyDatePicker from "../util/VuetifyDatepicker.vue";
import VuetifyTimePicker from "../util/VuetifyTimepicker.vue";
import Vue from "vue";
import UploadButton from "vuetify-upload-button";
import UploadImage from "@/components/util/UploadImage";
import HelpButton from "@/components/systemadmin/helpTopics/HelpButton";
const env = process.env;

export default {
  name: "create-ticket-modal",
  props: [
    "compact",
    "device",
    "hardwareItem",
    "selectedTicket",
    "ticketId",
    "batchMode",
  ],
  components: {
    HelpButton,
    VuetifyDatePicker,
    VuetifyTimePicker,
    UploadButton,
    UploadImage,
  },
  data() {
    return {
      Trimaster: Trimaster,
      isValid: false,
      rules: {
        required: (value) => {
          if (value instanceof Array && value.length == 0) {
            return "Required."
          }
          return !!value || "Required."
        }
      },
      label: null,
      adminOnly: false,
      showSummary: false,
      isBatchMode: true,
      currentDevices: [],
      selectedFacilityWorkerId: null,
      facilityWorkers: [],
      showSopSelector: false,
      selectedSops: [],
      currentHardwareItems: [],
      hardwareDevices: [],
      sops: [],
      selectedHardwareItems: [],
      lastDeviceTypeId: null,
      loadedTicket: null,
      selectedSubcomponent: null,
      subcomponents: [],
      displaySubcomponents: false,
      reportSources: [
        { id: "onsite", label: "On Site" },
        { id: "phone", label: "Phone" },
        { id: "email", label: "Email" },
        { id: "remoteMonitoring", label: "Remote Monitoring" },
      ],
      selectedSource: 'onsite',
      estimatedCompletionDate: null,
      statusesItems: [],
      hardwareItemTicketProblemTypes: [],
      selectedTicketStatusFilter: null,
      ticketsLoading: false,
      failureDate: moment().format("YYYY-MM-DD"),
      failureTime: moment().format("HH:mm"),
      ticketStatuses: [],
      hardware_item_ticket_problemType_id: 50,
      selectedNewTicketStatus: null,
      ticketDescription: "",
      active: null,
      selectedTicketStatus: 0,
      hideResolved: true,
      gtDate: "",
      ltDate: "",
      activeTab: 0,
      selectedStatus: {},
      currentTicket: {},
      isLoading: false,
      imageUrl: "",
      imageUrl2: "",
      imageUrl3: "",
      imageUrl4: "",
      savedHardwareItem: {},
      // currentType: "",
      headerMessage: "",
      showDevices: true,
      imageUploadInfo: {
        name: "hardware-item-ticket",
        uploadUrl: "hardware_item_tickets/s3Upload",
        bucketName: "hardware-item-tickets",
      },
      users: [],
      selectedUsers: [],
      ticketPriorities: [],
      selectedPriority: null,
      allFacilities: [],
      selectedFacilityId: null,
    };
  },
  beforeMount() {
    let me = this;

    // coming from the vfu screen
    if (this.device) {
      this.selectedHardwareItems.push({ ...this.device.id });
    } else {
      this.headerMessage = "";
    }

    this.getTicketStatuses();
    this.loadTicketPriorities();
    this.getHardwareItemTicketProblemTypes();

    // short-circuit to load record by id
    if (this.selectedTicket) {
      this.loadedTicket = this.selectedTicket;
      this.refresh();
    }

    this.isLoading = true;
    let promises = [
      Trimaster.get(
        "ag_userFacilities/getAllFacilityWorkers",
        this.getUser.apitoken
      ),
      Trimaster.get("StandardOperatingProcedures", this.getUser.apitoken, {
        filter: {
          fields: ["id", "label"],
          sort: "label",
        },
      }),
      Trimaster.get("hardware_items", me.getUser.apitoken, {
        filter: {
          where: {
            facilities_id: this.getUser.facility_id,
          },
          include: "device",
          order: "label",
          limit: 500,
        },
      }),
      Trimaster.get(
        "vFacilityUsers",
        this.getUser.apitoken,
        {
          filter: {
            order: 'firstname',
            where: {
              facility_id: this.getUser.facility_id,
              email: { like: "%agrify.com" },
            },
            include: ['roles']
          },
        }
      ),
      Trimaster.get("facilities", this.getUser.apitoken, {
        filter: {
          order: "name",
          where: {
            active: 1
          }
        }
      })
    ];

    Promise.all(promises).then(function ([[q1, err], [q2, err2], [q3, err3], [agrifyUsers,err4], [facilities, err5]]) {

      me.users = agrifyUsers;

      // get all facility workers
      for (let i = 0; i < q1.length; i++) {
        q1[i].label = q1[i].firstname + " " + q1[i].lastname;
      }
      q1.sort(function (a, b) {
        if (a.label > b.label) {
          return 1;
        } else {
          return -1;
        }
      });
      me.facilityWorkers = q1;

      // sops
      me.sops = q2;

      // hardware items
      me.hardwareDevices = q3;

      facilities.unshift({
        id: null,
        name: "(none)"
      });
      me.allFacilities = facilities;

      if (me.ticketId) {
        me.loadTicket();
        me.loadAssignments();
      } else {
        if (me.hardwareItem) {
          me.isBatchMode = false;
          me.selectedHardwareItems = me.hardwareDevices.find(
            (a) => a.id == me.hardwareItem.id
          );

          /* inactive
          if (me.hardwareItem.serialNumber) {
            me.currentType = me.hardwareItem.serialNumber.split("-")[0];
          } else {
            // no op
          }
          */
          me.savedHardwareItem = me.hardwareItem;
          me.getSubComponents(me.hardwareItem.hardware_product_id);
        }
        me.refresh();
        me.isLoading = false;
      }
    });
  },
  computed: {
    ...mapGetters(["getUser"]),

    titleStr() {
      if (this.selectedHardwareItems && this.selectedHardwareItems.length == 1) {
        if (typeof this.selectedHardwareItems[0] == "string") {
          return this.selectedHardwareItems[0];
        } else {
          return (
            this.selectedHardwareItems[0].platformGroupName ||
            this.selectedHardwareItems[0].label
          );
        }
      }

      return "";
    },

    isSoftwareTicket() {
      const selectedType = this.hardwareItemTicketProblemTypes.find((problemType) => {
        return problemType.id === this.hardware_item_ticket_problemType_id
      })
      return selectedType && selectedType.isSoftware
    },

    softwareDevUsers() {
      return this.users.filter(user => {
        return Trimaster.isSoftwareDev(user)
      })
    },

    nonSoftwareDevUsers() {
      return this.users.filter(user => {
        return !Trimaster.isSoftwareDev(user)
      })
    },

    assignableUsers() {
      const selectedType = this.hardwareItemTicketProblemTypes.find((problemType) => {
        return problemType.id === this.hardware_item_ticket_problemType_id
      })
      if (!selectedType) {
        return this.users
      } else if (selectedType.isSoftware) {
        return this.softwareDevUsers
      } else {
        return this.nonSoftwareDevUsers
      }
    }
  },
  methods: {
    async loadTicketPriorities() {
      let [statuses, err] = await Trimaster.get('ticket_priorities', this.getUser.apitoken)
      this.ticketPriorities = statuses.sort((a,b) => {
        return a.seq < b.seq ? -1 : 1
      })
    },
    loadTicket() {
      let me = this;
      me.isBatchMode = false;
      me.isLoading = true;
      Trimaster.get(
        "hardware_item_tickets/" + this.ticketId,
        this.getUser.apitoken,
        {
          filter: {
            include: ["hardware", "standardOperatingProcedure", "problemType"],
          },
        }
      ).then(function ([res, err]) {
        me.loadedTicket = res;
        me.label = res.label;
        me.selectedPriority = res.ticket_priority_id;
        if (res.hardware) {
          me.loadedTicket.hardware_item_id = res.hardware.id;
          me.selectedHardwareItems = me.hardwareDevices.find(
            (a) => a.id == res.hardware.id
          );
          me.displaySubcomponents = true;
        } else if (res.undocumentedHardware) {
          me.selectedHardwareItems = res.undocumentedHardware;
        }

        // note - res.undocumentedHardware is polymorphic
        if (res.standardOperatingProcedure) {
          me.selectedSops = res.standardOperatingProcedure;
          me.selectedFacilityWorkerId = res.sopOffendingUserId;
        }
        if (res.problemType.isSop && res.undocumentedHardware) {
          me.selectedSops = res.undocumentedHardware;
        }
        if (
          !res.problemType.isSop &&
          !res.problemType.isSoftware &&
          res.undocumentedHardware
        ) {
          me.selectedHardwareItems = res.undocumentedHardware;
        }

        me.refresh();
        me.isLoading = false;
      });
    },

    loadAssignments() {
      this.selected = [];

      let assignedUsersPromise = Trimaster.get(
        "hardware_item_ticket_assignments",
        this.getUser.apitoken,
        {
          filter: {
            where: {
              hardware_item_ticket_id: this.ticketId,
            },
          },
        }
      );

      Promise.all([
        assignedUsersPromise
      ]).then((responses) => {
        let assignedUsersIds = responses[0][0].map(assignedUser => assignedUser.user_id);
        this.selectedUsers = this.users.filter(eachUser => assignedUsersIds.indexOf(eachUser.id) !== -1);
      });
    },

    updateAssignments(ticketId) {
      return Trimaster.post("hardware_item_ticket_assignments/updateAssignments", this.getUser.apitoken, {
        hardware_item_ticket_id: ticketId,
        user_ids: this.selectedUsers.map(selectedUser => selectedUser.id),
      });
    },

    hardwareItemsSelected() {
      let rec = this.hardwareItemTicketProblemTypes.find(
        (a) => a.id == this.hardware_item_ticket_problemType_id
      );
      if (!rec || rec.isSoftware || rec.isSop) {
        return false;
      } else {
        return (
          !this.hardware_item_ticket_problemType_id ||
          this.selectedHardwareItems.length === 0
        );
      }
    },
    toggleDevices(val) {
      let rec = this.hardwareItemTicketProblemTypes.find((a) => a.id == val);

      if (rec) {
        if (rec.isSoftware) {
          this.showDevices = false;
          this.selectedHardwareItems = [];
          this.selectedSubcomponent = null;
          this.showSopSelector = false;
          this.showSummary = true;
        }

        if (rec.isSop) {
          this.showDevices = false;
          this.selectedHardwareItems = [];
          this.selectedSubcomponent = null;
          this.showSopSelector = true;
          this.showSummary = false;
        }

        if (!rec.isSop && !rec.isSoftware) {
          this.showSopSelector = false;
          this.showDevices = true;
          this.showSummary = false;
        }
      }
    },

    saveTicket() {
      this.$refs.newTicketForm.validate()
      if (!this.isValid || this.hardwareItemsSelected()) return

      let me = this;

      me.isLoading = true;
      let failureDate = moment(`${this.failureDate} ${this.failureTime}`).utc();
      let tickets = [];

      let ticketType = this.hardwareItemTicketProblemTypes.find(
        (a) => a.id == me.hardware_item_ticket_problemType_id
      );

      // update vs insert

      if (me.loadedTicket && !me.isBatchMode) {
        let undocumentedHardware = null;
        let standardOperatingProcedure_id = null;
        let hardware_item_id = null;

        // combobox handling
        if (ticketType.isSoftware) {
          // no op for now
        } else if (ticketType.isSop) {
          if (isNaN(me.standardOperatingProcedure_id)) {
            undocumentedHardware = me.standardOperatingProcedure_id;
          } else {
            standardOperatingProcedure_id = me.standardOperatingProcedure_id;
            undocumentedHardware = null;
          }
        } else {
          // hardware
          if (isNaN(me.selectedHardwareItems.id)) {
            undocumentedHardware = me.selectedHardwareItems;
          } else {
            undocumentedHardware = null;
            hardware_item_id = me.selectedHardwareItems.id;
          }
        }

        Trimaster.patch("hardware_item_tickets", this.getUser.apitoken, {
          id: me.loadedTicket.id,
          label: me.label,
          sopOffendingUserId: me.selectedFacilityWorkerId,
          hardware_item_ticket_problemType_id:
            me.hardware_item_ticket_problemType_id,
          ticket_priority_id: me.selectedPriority,
          hardware_item_ticket_status_id: me.selectedNewTicketStatus,
          hardware_type_container_item_id: me.selectedSubcomponent,
          undocumentedHardware: undocumentedHardware,
          failureDate: me.failureDate,
          description: me.ticketDescription,
          adminOnly: me.adminOnly,
          hardware_item_id: hardware_item_id,
          standardOperatingProcedure_id: standardOperatingProcedure_id,
          imageUrl: me.imageUrl,
          imageUrl2: me.imageUrl2,
          imageUrl3: me.imageUrl3,
          imageUrl4: me.imageUrl4,
          users_id: me.getUser.id,
          reportSource: me.selectedSource,
          estimatedCompletionDate: me.estimatedCompletionDate ? moment(me.estimatedCompletionDate).format(
            "YYYY-MM-DD 23:59:59"
          ) : null,
        }).then(function (res) {
          me.updateAssignments(me.loadedTicket.id).then(() => {
            me.isLoading = false;
            me.$emit("onSave", res);
            vuetifyToast.success("Ticket updated succesfully");
          });
        });
      } else {
        if (ticketType.isSoftware) {
          tickets.push({
            label: this.label,
            hardware_item_ticket_status_id: this.selectedNewTicketStatus,
            ticket_priority_id: this.selectedPriority,
            hardware_item_ticket_problemType_id: this
              .hardware_item_ticket_problemType_id,
            failureDate: me.failureDate,
            failureTime: me.failureTime,
            description: this.ticketDescription,
            adminOnly: this.adminOnly,
            imageUrl: this.imageUrl,
            imageUrl2: this.imageUrl2,
            imageUrl3: this.imageUrl3,
            imageUrl4: this.imageUrl4,
            users_id: me.getUser.id,
            facility_id: Trimaster.isSoftwareDev(me.getUser) ? me.selectedFacilityId : me.getUser.facility_id,
            reportSource: me.selectedSource,
            estimatedCompletionDate: me.estimatedCompletionDate ? moment(me.estimatedCompletionDate).format(
              "YYYY-MM-DD 23:59:59"
            ) : null,
            undocumentedHardware: null,
          });
        } else if (ticketType.isSop) {
          this.selectedSops.forEach((item) => {
            let hardware_item_id = null;
            let undocumentedHardware = null;
            let standardOperatingProcedure_id = null;

            if (item.id) {
              standardOperatingProcedure_id = item.id;
            } else if (
              this.loadedTicket &&
              this.loadedTicket.standardOperatingProcedure.id
            ) {
              standardOperatingProcedure_id = this.loadedTicket
                .standardOperatingProcedure.id;
            } else {
              undocumentedHardware = item;
            }

            tickets.push({
              hardware_item_id: null,
              label: this.label,
              standardOperatingProcedure_id: standardOperatingProcedure_id,
              undocumentedHardware: undocumentedHardware,
              hardware_item_ticket_status_id: this.selectedNewTicketStatus,
              ticket_priority_id: this.selectedPriority,
              hardware_item_ticket_problemType_id: this
                .hardware_item_ticket_problemType_id,
              failureDate: failureDate,
              description: this.ticketDescription,
              adminOnly: this.adminOnly,
              hardware_type_container_item_id: this.selectedSubcomponent,
              imageUrl: this.imageUrl,
              imageUrl2: this.imageUrl2,
              imageUrl3: this.imageUrl3,
              imageUrl4: this.imageUrl4,
              users_id: me.getUser.id,
              sopOffendingUserId: this.selectedFacilityWorkerId,
              reportSource: me.selectedSource,
              estimatedCompletionDate: me.estimatedCompletionDate ? moment(
                me.estimatedCompletionDate
              ).format("YYYY-MM-DD 23:59:59") : null,
              facility_id: me.getUser.facility_id,
            });
          });
        } else {
          let hwItems = null;
          if (!Array.isArray(this.selectedHardwareItems)) {
            hwItems = [this.selectedHardwareItems];
          } else {
            hwItems = this.selectedHardwareItems;
          }
          hwItems.forEach((item) => {
            let hardware_item_id = null;
            let undocumentedHardware = null;

            if (item.id) {
              hardware_item_id = item.id;
            } else if (this.loadedTicket && this.loadedTicket.hardware.id) {
              hardware_item_id = this.loadedTicket.hardware.id;
            } else {
              undocumentedHardware = item;
            }

            tickets.push({
              hardware_item_id: hardware_item_id,
              label: this.label,
              sopOffendingUserId: null,
              undocumentedHardware: undocumentedHardware,
              hardware_item_ticket_status_id: this.selectedNewTicketStatus,
              ticket_priority_id: this.selectedPriority,
              hardware_item_ticket_problemType_id: this
                .hardware_item_ticket_problemType_id,
              failureDate: failureDate,
              description: this.ticketDescription,
              adminOnly: this.adminOnly,
              hardware_type_container_item_id: this.selectedSubcomponent,
              imageUrl: this.imageUrl,
              imageUrl2: this.imageUrl2,
              imageUrl3: this.imageUrl3,
              imageUrl4: this.imageUrl4,
              users_id: me.getUser.id,
              reportSource: me.selectedSource,
              estimatedCompletionDate: me.estimatedCompletionDate ? moment(
                me.estimatedCompletionDate
              ).format("YYYY-MM-DD 23:59:59") : null,
              facility_id: me.getUser.facility_id,
            });
          });
        }

        // create new tickets
        Trimaster.post(
          "hardware_item_tickets",
          this.getUser.apitoken,
          tickets
        ).then(function (res) {
          if (res[0] && res[0].length) {
            let actions = [];
            res[0].forEach((ticket) => {
              actions.push({
                hardware_item_ticket_id: ticket.id,
                hardware_item_ticket_status_id:
                  ticket.hardware_item_ticket_status_id,
                ticket_priority_id: ticket.ticket_priority_id,
                actionPerformedBy_user_id: me.getUser.id,
                description: ticket.description,
                adminOnly: ticket.adminOnly,
              });
            });
            Trimaster.post(
              "hardware_item_ticket_actions",
              me.getUser.apitoken,
              actions
            ).then(function ([res,err]) {
              // TODO: may need to loop through ALL tickets created, not just one.
              if (res && res.length) {
                me.updateAssignments(res[0].hardware_item_ticket_id).then(() => {
                  me.$emit("onSave", res);
                  me.isLoading = false;
                  vuetifyToast.success("Tickets created successfully");
                });
              }
            });
          } else {
            vuetifyToast.error("Something went wrong, please try again.");
          }
          me.isLoading = false;
        });
      }
    },

    getSubComponents(productId) {
      if (productId) {
        let me = this;
        me.isLoading = true;

        Trimaster.get(
          "hardware_items/getContainerItemsByProductId",
          me.getUser.apitoken,
          {
            productId: productId,
          }
        ).then(function ([res3, err]) {
          res3.sort(function (a, b) {
            if (a.label > b.label) {
              return 1;
            } else {
              return -1;
            }
          });
          me.subcomponents = res3;
          me.isLoading = false;
        });
      } else {
        this.subcomponents = [];
      }
    },

    getTicketStatuses() {
      let me = this;
      Trimaster.get("hardware_item_ticket_statuses", me.getUser.apitoken, {
        filter: {
          order: "seq ASC",
        },
      }).then(function ([results, err]) {
        me.ticketStatuses = results;
        if (me.selectedTicket) {
          me.selectedNewTicketStatus =
            me.selectedTicket.hardware_item_ticket_status_id;
        } else {
          me.selectedNewTicketStatus = results[0].id;
        }
        me.isLoading = false;
      });
    },

    getHardwareItemTicketProblemTypes() {
      let me = this;
      me.isLoading = true;
      Trimaster.get("hardware_item_ticket_problemTypes", me.getUser.apitoken, {
        filter: {
          order: "label ASC",
        },
      }).then(function ([results, err]) {
        me.hardwareItemTicketProblemTypes = results;
        me.isLoading = false;
      });
    },
    getTicketDate(ticket) {
      return moment(ticket.failureDate).format("MM-DD-YYYY");
    },
    refresh() {
      let currentTicket = this.selectedTicket || this.loadedTicket;

      if (currentTicket) {
        this.failureDate = moment(currentTicket.failureDate).format(
          "YYYY-MM-DD"
        );
        this.failureTime = moment(currentTicket.failureDate).format("HH:mm");
        this.hardware_item_ticket_problemType_id =
          currentTicket.hardware_item_ticket_problemType_id;
        this.selectedSubcomponent =
          currentTicket.hardware_type_container_item_id;
        this.imageUrl = currentTicket.imageUrl || "";
        this.imageUrl2 = currentTicket.imageUrl2 || "";
        this.imageUrl3 = currentTicket.imageUrl3 || "";
        this.imageUrl4 = currentTicket.imageUrl4 || "";
        this.ticketDescription = currentTicket.description;
        this.adminOnly = currentTicket.adminOnly;
        this.selectedSource = currentTicket.reportSource;
        if (currentTicket.estimatedCompletionDate) {
          this.estimatedCompletionDate = moment(
            currentTicket.estimatedCompletionDate
          ).format("YYYY-MM-DD");
        } else {
          this.estimatedCompletionDate = null;
        }
      }
    },
    getTicketTime(ticket) {
      return moment(ticket.failureDate).format("hh:mm a");
    },
    fileChanged(file) {
      let type = file.type.split("/")[1];
      if (type == "png" || type == "mp4" || type == "jpg" || type == "jpeg") {
        let vm = this;
        vm.isLoading = true;

        let formData = new FormData();

        let originalName = file.name.split(".");

        let blob = file.slice(0, file.size, file.type);
        file = new File(
          [blob],
          `${
            env.VUE_APP_trimaster
          }-hardware-item-ticket-${Math.random().toString(36).substring(7)}-${
            vm.getUser.id
          }.${originalName[originalName.length - 1]}`,
          {
            type: file.type,
          }
        );

        formData.append("file", file);

        Trimaster.post(
          `hardware_item_tickets/s3Upload`,
          vm.getUser.apitoken,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
          {
            bucket: "hardware-item-tickets",
          }
        ).then(function (data) {
          vm.isLoading = false;
          let path = data[0][0];
          vm.imageUrl = path;
          vuetifyToast.success("File has been uploaded!");
        });
      } else {
        vuetifyToast.error(
          "File type is invalid! Please upload png, jpg, jpeg, or mp4 files."
        );
      }
    },
  },
  watch: {
    batchMode(newVal, oldVal) {
      this.isBatchMode = newVal;
    },
    hardware_item_ticket_problemType_id(newVal, oldVal) {
      this.toggleDevices(newVal);
    },
    currentDevices() {
      this.refresh();
    },
    ticketId(newVal, oldVal) {
      if (newVal) {
        this.loadTicket(newVal);
      }
    },
    selectedHardwareItems(val) {
      if (Array.isArray(val)) {
        let uniqueDeviceIds = [
          ...new Set(val.map((item) => item.hardware_product_id)),
        ];
        if (uniqueDeviceIds.length == 1) {
          this.displaySubcomponents = true;
          if (this.lastDeviceTypeId != uniqueDeviceIds[0]) {
            this.lastDeviceTypeId = uniqueDeviceIds[0];
            this.getSubComponents(this.lastDeviceTypeId);
          }
        } else {
          this.displaySubcomponents = false; // more than 1 distinct hardware type
        }
      } else if (typeof val === "object") {
        this.getSubComponents(val.hardware_product_id);
        this.displaySubcomponents = true;
      }
    },

    ticketStatuses(val) {
      this.statusesItems = [
        {
          id: 0,
          label: "All Statuses",
        },
      ];
      this.selectedTicketStatusFilter = 0;
      if (val && val.length) {
        val.forEach((status) => {
          this.statusesItems.push(status);
        });
      }
    },
  },
};
</script>

<style lang="scss">
#ticketEditor {
  .new-ticket-container {
    margin-top: 15px;
    overflow: auto;
  }

  .fullHeight {
    height: 100%;
  }

  .tickets-container {
    padding-top: 0;
  }

  .tickets-container .v-text-field {
    padding-top: 0px;
  }

  .tickets-container .v-input--selection-controls {
    margin-top: 0px;
  }

  .tickets-container .row {
    margin: 0px !important;
  }

  .tickets-container label {
    margin-top: 0px !important;
    margin-bottom: 0px !important;
    margin-left: 0px !important;
  }

  .tickets-container .v-input--selection-controls__input {
    margin-right: 0px !important;
  }

  .tickets-container .momentumscrolling {
    -webkit-overflow-scrolling: touch;
  }

  .tickets-title {
    padding-bottom: 0;
  }

  .tickets-buttons-container {
    text-align: right;
    padding: 15px 0 15px 0;
  }

  .ticket-description {
    margin-top: 15px;
  }
}
</style>
